import type { Location } from '@remix-run/router';
import { NavigateFunction } from 'react-router/dist/lib/hooks';
import { AuthContextType } from 'src/contexts/auth/jwt-context';
import { e2eLogic_getValidE2eLenders } from 'src/e2e-redesign/business-logic/e2e-business-logic';
import {
  e2eDiscLogic_chunkDisclosures,
  e2eDiscLogic_hasAcceptedAllDisclosures,
} from 'src/e2e-redesign/business-logic/e2e-disclosure-logic';
import {
  e2eSubLogic_handleKickoff,
  e2eSubLogic_kickOffNoDisplay,
} from 'src/e2e-redesign/business-logic/e2e-submission-logic';
import { ApplicationStage } from 'src/enums/aaso.enums';
import { BreadCFARoutes, E2eApprovalRoutes, E2ERoutes, ErrorRoutes, NewE2ERoutes } from 'src/enums/routes.enums';
import { AASOContextType } from 'src/hooks/use-aaso';
import { AASO, ApplicationEvents } from 'src/interfaces/aaso.interfaces';
import { ApplicationSettingsI } from 'src/interfaces/application-settings.interfaces';
import { E2eAppConfigurationsI, E2eGlobalStore } from 'src/interfaces/e2e-state.interfaces';
import { BREAD_LENDER_ID } from 'src/utils/bread-e2e-constants';

// import { routeTransitionMap, stageTransitionMap, validTransitions } from 'src/interfaces/transition-maps';

export interface E2eUrlAasoParamsI extends E2eUrlParamsI {
  aaso_id: string;
}

export interface E2eUrlParamsI {
  store_name: string;
  store_uuid: string;
  e2e_form_id: string;
}
export interface E2eRoutingLogicI {
  auth: AuthContextType;
  e2eStore: E2eGlobalStore;
  aasoContext: AASOContextType;
  navigate: NavigateFunction;
  location: Location;
  params?: E2eUrlParamsI | E2eUrlAasoParamsI;
}

export interface E2eRoutingArgsI {
  currentStage: ApplicationStage;
  latest_applicant_event?: ApplicationEvents;
  currentE2eRoute: E2ERoutes | NewE2ERoutes | BreadCFARoutes;
  aaso?: Partial<AASO>;
  continueRoute?: string;
  continue?: boolean;
}
// const d = stageTransitionMap;
// const e = routeTransitionMap;
// const b = validTransitions;

const approvalPath = '/applicant-approve-offers/';
const getPublicE2ePath = (store_name: string, store_uuid: string, e2e_form_id: string, route: NewE2ERoutes) => {
  const storeName = encodeURIComponent(store_name);
  const storeUuid = encodeURIComponent(store_uuid);
  const e2eFormId = encodeURIComponent(e2e_form_id);
  /// '/:store_name/:store_uuid/:e2e_form_id/<whatever>',
  return `/${storeName}/${storeUuid}/${e2eFormId}/${route}`;
};

export interface ApprovalUrlParamsI extends Record<string, string | undefined> {
  fp_unique_id?: string;
  aaso_id?: string;
  form_id?: string;
  store_name?: string;
  e2e_form_id?: string;
  store_uuid?: string;
}
const getApprovalPath = (params: ApprovalUrlParamsI, route: E2eApprovalRoutes) => {
  const { fp_unique_id, aaso_id, form_id, store_name, e2e_form_id, store_uuid } = params;
  if (!fp_unique_id || !aaso_id || !form_id || !store_name || !e2e_form_id || !store_uuid)
    throw new Error('Missing required params');
  const fpUniqueId = encodeURIComponent(fp_unique_id);
  const storeName = encodeURIComponent(store_name);
  const storeUuid = encodeURIComponent(store_uuid);
  const aasoId = encodeURIComponent(aaso_id);
  const e2eFormId = encodeURIComponent(e2e_form_id);
  const formId = encodeURIComponent(form_id);

  /// '/:store_name/:store_uuid/:e2e_form_id/<whatever>',
  return `${approvalPath}${storeName}/${fpUniqueId}/${aasoId}/${storeUuid}/${e2eFormId}/${formId}/${route}`;
};
const getFullE2ePath = (
  store_name: string,
  store_uuid: string,
  aaso_id: string,
  e2e_form_id: string,
  route: NewE2ERoutes | string,
) => {
  const storeName = encodeURIComponent(store_name);
  const storeUuid = encodeURIComponent(store_uuid);
  const aasoId = encodeURIComponent(aaso_id);
  const e2eFormId = encodeURIComponent(e2e_form_id);
  // '/:store_name/:store_uuid/:e2e_form_id/:aaso_id/<whatever>'
  return `/${storeName}/${storeUuid}/${e2eFormId}/${aasoId}/${route}`;
};

export const handleE2eUnauthenticatedRoutingLogic = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI, newAaso?: AASO) => {
  const { currentStage } = args;
  const logicObjectsExist = verifyPrimaryFunctionsAndObjectsExist(obj);
  const appConfigurationObject = obj.e2eStore.app_configurations;

  if (logicObjectsExist && appConfigurationObject) {
    switch (currentStage) {
      case ApplicationStage.STARTED:
        handleUnauthenticatedWelcomeRouting(obj, args);
        break;
    }
  }
  // navigate(AuthRoutes.Login);
};

export const handleCreateAccountRoutingLogic = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI) => {
  const { e2eStore, navigate } = obj;
  const aaso = e2eStore.aaso;
  if (aaso && aaso.e2e_form_id) {
    navigate(`${getPublicE2ePath(aaso.store_name, aaso.store_uuid, aaso.e2e_form_id, NewE2ERoutes.CreateAccount)}`);
  }
};

export const e2eRouting_routeTo404 = (navigate: NavigateFunction) => {
  navigate('/' + ErrorRoutes.NotFound);
};
export const handleE2eRoutingLogic = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI, newAaso?: AASO) => {
  const { e2eStore } = obj;
  const { currentStage } = args;
  const logicObjectsExist = verifyPrimaryFunctionsAndObjectsExist(obj);
  const e2eStoreLogicObjectsExist = verifyE2eStoreLogicObjectsExist(e2eStore);

  if (logicObjectsExist && e2eStoreLogicObjectsExist) {
    switch (currentStage) {
      case ApplicationStage.STARTED:
        handleWelcomeRouting(obj, args, newAaso);
        break;
      case ApplicationStage.FLOW_STARTED:
        handleGetStartedRouting(obj, args, newAaso);
        break;
      case ApplicationStage.SELECTED_PATH:
        handlePathSelectedGetStarted(obj, args, newAaso);
        break;
      case ApplicationStage.DISCLOSURE_LENDERS:
      case ApplicationStage.FORM_STARTED:
        handleLenderDisclosureRouting(obj, args, newAaso);
        break;
      case ApplicationStage.FORM_SUBMITTED:
        handleE2eProviderResponseRouting(obj, args, newAaso);
        break;
      case ApplicationStage.INITIAL_SELECTION:
      case ApplicationStage.SUBMITTED_TO_LENDER:
        handleE2eProviderResponseRouting(obj, args, newAaso);
        break;
      case ApplicationStage.LENDER_SELECTED:
        handleE2eUserCompletedInitialSelectionRouting(obj, args);
    }
  }
  // navigate(AuthRoutes.Login);
};

export const handleE2eUserCompletedInitialSelectionRouting = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI) => {
  const { navigate } = obj;
  navigate(`/${E2ERoutes.Dashboard}`, { replace: true });
};

export const handleE2eApplicantApprovedNewOfferRouting = (navigate: NavigateFunction, params: ApprovalUrlParamsI) => {
  const path = getApprovalPath(params, E2eApprovalRoutes.ThankYou);
  navigate(path, { replace: true });
};
export const handleE2eProviderResponseRouting = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI, newAaso?: AASO) => {
  const { e2eStore, navigate } = obj;
  const aaso = e2eStore.aaso;
  const appSettings: ApplicationSettingsI = e2eStore.application_settings!;
  const e2eForms = appSettings?.e2e?.e2e_forms;
  if (e2eStore.application_settings) {
    if (newAaso && newAaso._id && newAaso.e2e_form_id) {
      // user is selecting to continue an existing application
      navigate(
        getFullE2ePath(
          newAaso.store_name,
          newAaso.store_uuid,
          newAaso._id,
          newAaso.e2e_form_id,
          NewE2ERoutes.ProviderResponses,
        ),
        { replace: true }, // We don't want the user going back
      );
    } else {
      if (aaso && aaso._id && aaso.e2e_form_id) {
        navigate(
          getFullE2ePath(aaso.store_name, aaso.store_uuid, aaso._id, aaso.e2e_form_id, NewE2ERoutes.ProviderResponses),
          { replace: true }, // We don't want the user going back
        );
      }
    }
  }
};
export const handleE2eFormSubmittedRoutingLogic = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI) => {
  const { e2eStore, navigate } = obj;
  const aaso = obj.aasoContext?.aaso;

  if (aaso) {
    const { store_uuid, store_name, _id, e2e_form_id } = aaso;
    if (_id && e2e_form_id) {
      const appConfig = obj.e2eStore.app_configurations;
      if (appConfig) {
        const currentPath = appConfig?.current_path;
        // grab all auto_submits and not
        if (currentPath) {
          // TODO - need to change this to && instead of ||
          // includeAutoSubmit is it handle the new cfa_config object
          const includeAutoSubmit = 'display_offers' in currentPath;
          if (includeAutoSubmit) {
            // it's the new cfa
            const e2eLenders = e2eLogic_getValidE2eLenders(
              appConfig.lenders,
              appConfig.current_path,
              includeAutoSubmit,
            );
            if (e2eLenders.length > 0 && currentPath?.display_offers) {
              navigate(getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.ProviderResponses), {
                replace: true,
              });
            } else {
              // TODO - add toggle auto-submit here
              const hasAutoSubmits = currentPath.lender_details.some((ld) => ld.auto_submit);
              if (hasAutoSubmits) {
                e2eSubLogic_kickOffNoDisplay({ aaso_id: _id, e2e_form_id, store_uuid });
              }
              const path = getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.ThankYou);
              navigate(path, { replace: true });
            }
          } else {
            // old object
            const e2eLenders = e2eLogic_getValidE2eLenders(appConfig.lenders, appConfig.current_path);
            if (e2eLenders.length > 0) {
              navigate(getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.ProviderResponses), {
                replace: true,
              });
            } else {
              const path = getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.ThankYou);
              navigate(path, { replace: true });
            }
          }
        }
      }
    }
  }
};
export const handleLenderDisclosureRouting = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI, newAaso?: AASO) => {
  const { e2eStore, navigate } = obj;
  const appSettings: ApplicationSettingsI = e2eStore.application_settings!;
  const aaso = obj.aasoContext?.aaso;
  /**
   * We must verify that all disclosures have been signed, as this might change
   */
  if (aaso) {
    const e2eForm = appSettings.e2e?.e2e_forms.find((form) => form.id === aaso.e2e_form_id);
    const e2ePath = e2eForm?.paths.find((p) => p.id === aaso.current_path_id);
    const chunked = e2eDiscLogic_chunkDisclosures(e2eStore.disclosures, e2eStore.lenders, e2ePath);
    const allDiscsAccepted = e2eDiscLogic_hasAcceptedAllDisclosures(chunked, aaso.disclosures);
    const { store_uuid, store_name, _id, e2e_form_id } = aaso;
    if (_id && e2e_form_id) {
      if (allDiscsAccepted) {
        // all disclosures have been accepted, go to forms
        navigate(getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.Application), { replace: true });
      } else {
        // go to provider disclosures
        navigate(getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.ProviderDisclosures));
      }
    }
  }
};

export const handleCancelApplicationRouting = (obj: E2eRoutingLogicI) => {
  const { e2eStore, navigate, location } = obj;
  const aaso = obj.aasoContext?.aaso;

  if (aaso) {
    const { store_uuid, store_name, e2e_form_id } = aaso;
    if (e2e_form_id) {
      // Get the mLink parameter from the current URL if it exists
      const currentSearchParams = new URLSearchParams(location.search);
      const mLink = currentSearchParams.get('mLink');

      const path = getPublicE2ePath(store_name, store_uuid, e2e_form_id, NewE2ERoutes.LandingPage);
      navigate(mLink ? `${path}?mLink=${mLink}` : path);
      /**
       * We need to remove the old AASO, because it's grabbing it, but
       * right now we don't know everything that is required to be removed so
       * the best solution right now is to just reload the page entirely
       */
      // e2eStore.setAASO({
      //   ...aaso,
      //   _id: undefined,
      // });
      // TODO - fix - this is a hacky solution to prevent the user from "Starting New App" and going back to the last one
      window.location.reload();
    }
  }
};

const handleGetStartedRouting = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI, newAaso?: AASO) => {
  const { e2eStore, navigate } = obj;
  const aaso = obj.aasoContext?.aaso;
  if (aaso) {
    const { store_uuid, store_name, _id, e2e_form_id } = aaso;
    if (_id && e2e_form_id) {
      navigate(getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.ProviderDisclosures));
    }
  }
};

const handlePathSelectedGetStarted = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI, newAaso?: AASO) => {
  const { e2eStore, navigate, aasoContext } = obj;
  const aaso = aasoContext.aaso;
  if (e2eStore.application_settings) {
    if (aaso && aaso._id && aaso.e2e_form_id && aaso.current_path_id) {
      // THIS is a multi-path and user has selected, so send them to GetStarted
      navigate(getFullE2ePath(aaso.store_name, aaso.store_uuid, aaso._id, aaso.e2e_form_id, NewE2ERoutes.GetStarted));
    }
  }
};

export const handleE2eLoginLogicRoute = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI, e2eFormId?: string) => {
  /**
   * Modal logic
   */
  const { navigate } = obj;
  const aaso = args.aaso;
  const cont = args.continue;
  const continueRoute = args.continueRoute;

  if (aaso && continueRoute && e2eFormId) {
    const { store_name, store_uuid, _id } = aaso;
    if (store_name && store_uuid && _id) {
      if (cont) {
        // path += `${store_name}/${store_uuid}/${_id}/${e2eFormId}/${continueRoute}`;
        navigate(getFullE2ePath(store_name, store_uuid, _id, e2eFormId, continueRoute));
        return;
      } else {
        // path += `${aaso.store_name}/${aaso.store_uuid}/${e2eFormId}/${aaso._id}/${continueRoute.split('/').pop()}`;
        const newRoute = continueRoute.split('/').pop();
        if (newRoute) {
          navigate(getFullE2ePath(store_name, store_uuid, _id, e2eFormId, newRoute));
        }
      }
    }
  } else {
    // catch all - send to dashboard
    navigate(`/${E2ERoutes.Dashboard}`, { replace: true });
  }
};
const handleWelcomeRouting = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI, newAaso?: AASO) => {
  const { e2eStore, navigate, aasoContext } = obj;
  const aaso = aasoContext.aaso;
  if (e2eStore.application_settings) {
    const lenderLineup = e2eStore.app_configurations?.lender_ids;
    if (lenderLineup && lenderLineup.includes(BREAD_LENDER_ID)) {
      // TODO - switch to user Bread ID
      /**
       * NOTE:
       * - this is a temporary solution for the bread CFA. This directs the user to use
       * the BreadCFARoutes
       */
      if (aaso && aaso._id && aaso.e2e_form_id && aaso.current_path_id) {
        // R:TODO E2E P0 - refactor
        // THIS is a multi-path and user has selected, so send them to GetStarted
        navigate(
          getFullE2ePath(aaso.store_name, aaso.store_uuid, aaso._id, aaso.e2e_form_id, BreadCFARoutes.GetStarted),
        );
      } else {
        const appConfig: E2eAppConfigurationsI = e2eStore.app_configurations!;
        const route = appConfig.multi_paths_exist ? BreadCFARoutes.SelectApplicationType : BreadCFARoutes.GetStarted;
        if (newAaso) {
          // user is selecting to continue an existing application
          if (newAaso._id && newAaso.e2e_form_id) {
            // TODO - continuing a bread cfa app is broken and needs to be fixed
            navigate(getFullE2ePath(newAaso.store_name, newAaso.store_uuid, newAaso._id, newAaso.e2e_form_id, route), {
              replace: true,
            });
          }
        } else {
          navigate(route);
        }
      }
    } else {
      if (aaso && aaso._id && aaso.e2e_form_id && aaso.current_path_id) {
        // R:TODO E2E P0 - refactor
        // THIS is a multi-path and user has selected, so send them to GetStarted
        navigate(getFullE2ePath(aaso.store_name, aaso.store_uuid, aaso._id, aaso.e2e_form_id, NewE2ERoutes.GetStarted));
      } else {
        const appConfig: E2eAppConfigurationsI = e2eStore.app_configurations!;
        const route = appConfig.multi_paths_exist ? NewE2ERoutes.SelectApplicationType : NewE2ERoutes.GetStarted;
        if (newAaso) {
          // user is selecting to continue an existing application
          if (newAaso._id && newAaso.e2e_form_id) {
            navigate(getFullE2ePath(newAaso.store_name, newAaso.store_uuid, newAaso._id, newAaso.e2e_form_id, route), {
              replace: true,
            });
          }
        } else {
          navigate(route);
        }
      }
    }
  }
};

const handleUnauthenticatedWelcomeRouting = (obj: E2eRoutingLogicI, args: E2eRoutingArgsI) => {
  const { e2eStore, navigate } = obj;
  const hasBread = e2eStore.disclosures.find((d) => Boolean(d?.lender_id == BREAD_LENDER_ID));
  const appConfig: E2eAppConfigurationsI = e2eStore.app_configurations!;

  if (hasBread) {
    return navigate(BreadCFARoutes.GetStarted);
  }
  if (appConfig) {
    const route = appConfig.multi_paths_exist ? NewE2ERoutes.SelectApplicationType : NewE2ERoutes.GetStarted;
    return navigate(route);
  }
  throw new Error('Failed to route unauthorized user to welcome routing');
};

const verifyPrimaryFunctionsAndObjectsExist = (obj: E2eRoutingLogicI) => {
  const { auth, e2eStore, aasoContext, navigate, location } = obj;
  if (!auth || !e2eStore || !aasoContext || !navigate || !location) {
    return false;
  }
  return true;
};
const verifyE2eStoreLogicObjectsExist = (e2eStore: E2eGlobalStore) => {
  if (
    e2eStore.application_settings &&
    e2eStore.lenders &&
    e2eStore.store_settings &&
    e2eStore.store_details &&
    e2eStore.outside_form_details
  ) {
    return true;
  }
  return false;
};
